import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Users, Trophy, ClipboardList, ShieldCheck } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useAuth, isStaff } from "@/context/AuthContext";
import { api, errMsg, usePageTitle } from "@/lib/api";
import { IMG, DIVISIONS } from "@/lib/content";
import { PalmSilhouette } from "@/components/site/Bits";

const Field = ({ id, label, ...p }) => (
  <div><label className="label" htmlFor={id}>{label}</label><input id={id} className="field" data-testid={id} {...p} /></div>
);

const SignIn = ({ onDone }) => {
  const { login } = useAuth();
  const [f, setF] = useState({ email: "", password: "" });
  const [busy, setBusy] = useState(false);
  const [forgot, setForgot] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (forgot) {
        await api.post("/auth/forgot-password", { email: f.email });
        toast.success("If that email has an account, a reset link is on its way. Check your inbox (and spam folder).");
        setForgot(false);
      } else onDone(await login(f.email, f.password));
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <form onSubmit={submit} className="space-y-5" data-testid="login-form">
      <Field id="login-email" label="Email" type="email" required value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} />
      {!forgot && <Field id="login-password" label="Password" type="password" required value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} />}
      <button type="submit" disabled={busy} className="btn-gold w-full" data-testid="login-submit-button">{busy && <Loader2 className="h-4 w-4 animate-spin" />}{forgot ? "Send Reset Link" : "Sign In"}</button>
      <button type="button" onClick={() => setForgot(!forgot)} className="text-xs uppercase tracking-wider text-slate-400 hover:text-gold" data-testid="login-forgot-toggle">{forgot ? "Back to sign in" : "Forgot password?"}</button>
    </form>
  );
};

const ROLES = [
  { k: "parent", label: "Parent / Guardian", icon: Users, note: "Track your athlete's registration, payments and private team updates." },
  { k: "player", label: "Player", icon: Trophy, note: "Your own login for team updates and your registration status." },
  { k: "coach", label: "Coach", icon: ClipboardList, note: "Manage your division's roster, schedule and parent updates once approved." },
  { k: "admin", label: "Admin", icon: ShieldCheck, note: "Leadership access. Must be approved by Coach KP before it works." },
];

const Register = ({ onDone }) => {
  const { register } = useAuth();
  const [f, setF] = useState({ role: "parent", name: "", email: "", phone: "", password: "", team_slug: "", athletes: "" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const role = ROLES.find((r) => r.k === f.role);
  const submit = async (e) => {
    e.preventDefault();
    if ((f.role === "coach" || f.role === "player") && !f.team_slug) return toast.error("Please choose a division.");
    setBusy(true);
    try {
      onDone(await register(f));
      toast.success("Account created. Tri County staff will approve your access.");
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <form onSubmit={submit} className="space-y-5" data-testid="register-form">
      <div>
        <p className="label">I am a…</p>
        <div className="grid grid-cols-2 gap-2" data-testid="register-role-picker">
          {ROLES.map(({ k, label, icon: Icon }) => (
            <button type="button" key={k} onClick={() => setF({ ...f, role: k })} data-testid={`register-role-${k}`}
              className={`flex items-center gap-2 border px-3 py-3 font-display text-base font-extrabold uppercase italic transition-colors ${f.role === k ? "bg-gold text-navy border-gold" : "border-line text-slate-300 hover:border-gold"}`}>
              <Icon className="h-4 w-4 shrink-0" /> {label}
            </button>
          ))}
        </div>
        <p className="mt-2 text-xs text-slate-400" data-testid="register-role-note">{role.note}</p>
      </div>
      <Field id="register-name" label={f.role === "parent" ? "Parent / Guardian Name" : f.role === "player" ? "Player Name" : "Full Name"} required value={f.name} onChange={set("name")} />
      <Field id="register-email" label="Email" type="email" required value={f.email} onChange={set("email")} />
      <Field id="register-phone" label="Phone (optional)" type="tel" value={f.phone} onChange={set("phone")} />
      <Field id="register-password" label="Password (8+ characters)" type="password" minLength={8} required value={f.password} onChange={set("password")} />
      {f.role !== "admin" && (
        <div>
          <label className="label" htmlFor="register-team">{f.role === "coach" ? "Division You Coach *" : f.role === "player" ? "Your Division *" : "Athlete's Division"}</label>
          <select id="register-team" className="field" value={f.team_slug} onChange={set("team_slug")} data-testid="register-team">
            <option value="">Select a division</option>
            {DIVISIONS.map((d) => <option key={d.slug} value={d.slug}>{d.name}</option>)}
          </select>
        </div>
      )}
      {f.role === "parent" && <Field id="register-athletes" label="Athlete Name(s)" placeholder="e.g. Jordan Smith" value={f.athletes} onChange={set("athletes")} />}
      <button type="submit" disabled={busy} className="btn-gold w-full" data-testid="register-submit-button">{busy && <Loader2 className="h-4 w-4 animate-spin" />}Create {role.label} Account</button>
      <p className="text-xs text-slate-500">Every account is reviewed and approved by Tri County Allstars staff before any private information or dashboard tools are available.</p>
    </form>
  );
};

export default function Login() {
  usePageTitle("Login");
  const { user } = useAuth();
  const nav = useNavigate();
  const go = (u) => nav(isStaff(u) ? "/admin" : "/portal");
  useEffect(() => {
    if (user) go(user);
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <section className="relative min-h-screen overflow-hidden bg-pitch pt-32 pb-16" data-testid="login-page">
      <img src={IMG.night} alt="" className="absolute inset-0 h-full w-full object-cover opacity-20" />
      <div className="absolute inset-0 bg-gradient-to-br from-navy/90 to-pitch" />
      <PalmSilhouette className="absolute right-0 bottom-0 h-[70%] text-pitch/70" />
      <div className="relative wrap grid gap-12 lg:grid-cols-2 items-center">
        <div>
          <p className="eyebrow">Secure Access</p>
          <h1 className="h-display text-5xl sm:text-7xl mt-3">Parents, Players,<br /><span className="text-gold">Coaches & Admins</span></h1>
          <p className="mt-5 max-w-md text-slate-300">One login for every member of the Tri County family. Parents and players see private team updates and registration status. Coaches manage their division. Administrators run the website.</p>
        </div>
        <div className="card-navy p-6 sm:p-8 max-w-md w-full lg:justify-self-end">
          <Tabs defaultValue="signin">
            <TabsList className="grid grid-cols-2 w-full bg-pitch rounded-none p-1 h-auto">
              <TabsTrigger value="signin" className="rounded-none py-2.5 font-display uppercase font-bold data-[state=active]:bg-gold data-[state=active]:text-navy" data-testid="login-tab-signin">Sign In</TabsTrigger>
              <TabsTrigger value="register" className="rounded-none py-2.5 font-display uppercase font-bold data-[state=active]:bg-gold data-[state=active]:text-navy" data-testid="login-tab-register">Create Account</TabsTrigger>
            </TabsList>
            <TabsContent value="signin" className="mt-6"><SignIn onDone={go} /></TabsContent>
            <TabsContent value="register" className="mt-6"><Register onDone={go} /></TabsContent>
          </Tabs>
        </div>
      </div>
    </section>
  );
}
